import { useRef } from "react";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";

import { Button } from "~/components";
import { T } from "@repo/transifex";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { useVerifyTicket } from "~/hooks/queries/tickets";
import { usePrintOrderReceipt } from "~/hooks/queries/orders";
import {
  ScreenError,
  ScreenErrorTitle,
  ScreenErrorText
} from "~/components/screen/screen-error";
import {
  ScreenSuccessText,
  ScreenSuccessTitle,
  ScreenSuccess
} from "~/components/screen/screen-success";
import { LayoutLock } from "~/components/layout/layout-lock";
import { logger } from "~/services/logger";
import { usePerformance } from "~/hooks/use-performance";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useScanner } from "~/hooks/use-scanner";
import { useAppContext, useAppDispatch } from "~/providers/app";

import { TicketScanSvg } from "./assets/TicketScanSvg";
import { TicketValidationOrderDetails } from "./ticket-validation-order-details";
import { TicketValidationReceiptPrinting } from "./ticket-validation-receipt-printing";

const OPERATION_NAME = "ticket-validation";

export const TicketValidationPage = () => {
  const { start, end } = usePerformance();
  const { data: till } = useCurrentTill();
  const dispatch = useAppDispatch();
  const {
    hardware: { printer }
  } = useAppContext();

  const {
    data: verificationResponse,
    mutate: verifyTicket,
    status: verificationStatus,
    reset
  } = useVerifyTicket();

  const { mutate: createReceipt } = usePrintOrderReceipt();

  const scannedOrderId = useRef<string | null>(null);

  const shouldAutoPrint =
    till?.type === "ticket-validation" &&
    till.config?.receiptPrintMode === "automatic" &&
    Boolean(printer);

  const handlePrintReceiptReset = () => {
    dispatch({ type: "PRINTER_PRINT_RECEIPT_RESET" });
    reset();
  };

  useScanner({
    onSuccess: (scannedCodePayload) => {
      if (verificationStatus === "pending") {
        logger.debug("ticket validation is pending, skipping", {
          operation_name: OPERATION_NAME
        });
        return;
      }

      if (verificationStatus === "success" || verificationStatus === "error") {
        reset();
      }

      start(OPERATION_NAME);

      logger.trace("scanned ticket code", {
        operation_name: OPERATION_NAME,
        operation_status: "start"
      });

      logger.debug("call api to verify ticket", {
        operation_name: OPERATION_NAME
      });

      verifyTicket(scannedCodePayload, {
        onSuccess: (response) => {
          logger.trace("verify ticket success", {
            is_valid: response.isValid,
            operation_name: OPERATION_NAME,
            operation_status: "success",
            operation_duration: end(OPERATION_NAME)
          });

          if (response.isValid && shouldAutoPrint) {
            scannedOrderId.current = scannedCodePayload.value;
            createReceipt({ orderId: scannedCodePayload.value, userId: null });
          }
        },
        onError: (error) => {
          logger.error(error, "error verifying ticket", {
            operation_name: OPERATION_NAME,
            operation_duration: end(OPERATION_NAME),
            operation_status: "error"
          });
        }
      });
    }
  });

  if (verificationStatus === "success" && verificationResponse.isValid) {
    if (
      till?.type === "ticket-validation" &&
      till?.config?.showPurchasedItemsOnConfirmationScreen
    ) {
      return (
        <TicketValidationOrderDetails
          onClose={handlePrintReceiptReset}
          orderDetails={verificationResponse.orderDetails}
          shouldAutoPrint={shouldAutoPrint}
          onRetry={() => {
            if (scannedOrderId.current) {
              createReceipt({ orderId: scannedOrderId.current, userId: null });
            }
          }}
        />
      );
    }
    return (
      <ScreenSuccess
        onClose={handlePrintReceiptReset}
        footer={
          shouldAutoPrint ? (
            <TicketValidationReceiptPrinting
              onRetry={() => {
                if (scannedOrderId.current) {
                  createReceipt({
                    orderId: scannedOrderId.current,
                    userId: null
                  });
                }
              }}
            />
          ) : undefined
        }
      >
        <ScreenSuccessTitle>
          <T _str="Ticket confirmed" />
        </ScreenSuccessTitle>

        <ScreenSuccessText>
          <T _str="Head over to collect your order. Enjoy your meal!" />
        </ScreenSuccessText>
      </ScreenSuccess>
    );
  }

  if (verificationStatus === "success" && !verificationResponse.isValid) {
    return (
      <ScreenError onClose={reset}>
        <ScreenErrorTitle>
          <T _str="Ticket expired" />
        </ScreenErrorTitle>

        <ScreenErrorText>
          <T _str="We couldn't find your order. Please try again or ask staff for help" />
        </ScreenErrorText>
      </ScreenError>
    );
  }

  if (verificationStatus === "error") {
    return (
      <ScreenError onClose={reset}>
        <ScreenErrorTitle>
          <T _str="Something went wrong" />
        </ScreenErrorTitle>

        <ScreenErrorText>
          <T _str="Please try again or ask staff for help" />
        </ScreenErrorText>
      </ScreenError>
    );
  }

  return (
    <LayoutLock>
      {verificationStatus === "pending" ? (
        <LoadingSpinner />
      ) : (
        <>
          <TicketScanSvg />
          <p className="w-8/12 text-center text-5xl font-bold">
            <T _str="Scan your order QR code — found in the app under your order or on your printed receipt" />
          </p>

          <Link to="help">
            <Button
              size="medium"
              className="bg-slate-50"
              textClassName="text-slate-900"
              icon={faInfoCircle}
            >
              <T _str="How you scan your ticket" />
            </Button>
          </Link>
        </>
      )}
    </LayoutLock>
  );
};
